import type { PostHog } from "posthog-js";

type PostHogClient = Pick<PostHog, "capture"> | null | undefined;

export type AdAccountConnectionPlatform =
  | "meta"
  | "google"
  | "msads"
  | "tiktok"
  | "pinterest"
  | "taboola"
  | "snapchat"
  | "criteo"
  | "outbrain";

export type AdAccountConnectionUpdateReason = "reauthorized" | "store_mapping";

/**
 * Snapshot of the accounts that were already connected for a platform when
 * the user started an OAuth connect flow. The ad-connector redirects back to
 * `/success?account_ids=a,b,c` with every account it authorized in that
 * session; diffing that list against this baseline tells us which accounts
 * are genuinely new (connected) and which were re-authorized (updated).
 *
 * Stored in sessionStorage because the OAuth round-trip is a full page
 * navigation to a third-party origin — no in-memory state survives it.
 */
type PendingAdAccountConnection = {
  platform: AdAccountConnectionPlatform;
  existingAccountIds: string[];
  createdAt: number;
};

const AD_ACCOUNT_CONNECTED_EVENT = "Ad account connected";
const AD_ACCOUNT_CONNECTION_UPDATED_EVENT = "Ad account connection updated";
const AD_ACCOUNT_CONNECTION_FAILED_EVENT = "Ad account connection failed";
const PENDING_CONNECTION_KEY = "tracify:pending-ad-account-connection";
const REPORTED_FAILURE_KEY = "tracify:reported-ad-account-connection-failure";
const PENDING_EVENT_MAX_AGE_MS = 30 * 60 * 1000;
const MAX_ERROR_MESSAGE_LENGTH = 500;

const platformAliases: Record<string, AdAccountConnectionPlatform> = {
  fb: "meta",
  facebook: "meta",
  meta: "meta",
  google: "google",
  msads: "msads",
  tiktok: "tiktok",
  pinterest: "pinterest",
  taboola: "taboola",
  snapchat: "snapchat",
  criteo: "criteo",
  outbrain: "outbrain",
};

export function normalizeAdAccountConnectionPlatform(
  platform: string
): AdAccountConnectionPlatform | null {
  return platformAliases[platform.toLowerCase()] ?? null;
}

/**
 * Parses the `account_ids` query param the ad-connector appends to OAuth
 * success redirects (`/success?account_ids=a,b,c`).
 */
export function parseAuthorizedAccountIds(
  value: string | string[] | null | undefined
): string[] {
  const raw = Array.isArray(value) ? value.join(",") : (value ?? "");
  return [
    ...new Set(
      raw
        .split(",")
        .map((accountId) => accountId.trim())
        .filter(Boolean)
    ),
  ];
}

// ---------------------------------------------------------------------------
// Capture helpers
// ---------------------------------------------------------------------------

export function captureAdAccountConnected(
  posthog: PostHogClient,
  platform: string,
  accountId: string
): boolean {
  const normalizedPlatform = normalizeAdAccountConnectionPlatform(platform);
  if (!normalizedPlatform || !accountId) {
    return false;
  }

  posthog?.capture?.(AD_ACCOUNT_CONNECTED_EVENT, {
    platform: normalizedPlatform,
    account_id: accountId,
  });
  return true;
}

export function captureAdAccountConnectionUpdated(
  posthog: PostHogClient,
  platform: string,
  accountId: string,
  reason: AdAccountConnectionUpdateReason
): boolean {
  const normalizedPlatform = normalizeAdAccountConnectionPlatform(platform);
  if (!normalizedPlatform || !accountId) {
    return false;
  }

  posthog?.capture?.(AD_ACCOUNT_CONNECTION_UPDATED_EVENT, {
    platform: normalizedPlatform,
    account_id: accountId,
    reason,
  });
  return true;
}

type AdAccountConnectionFailureOptions = {
  accountId?: string;
  errorCode?: string;
};

export function captureAdAccountConnectionFailed(
  posthog: PostHogClient,
  platform: string,
  errorMessage: string,
  options: AdAccountConnectionFailureOptions = {}
): boolean {
  const normalizedPlatform = normalizeAdAccountConnectionPlatform(platform);
  if (!normalizedPlatform || !errorMessage) {
    return false;
  }

  const { accountId, errorCode } = options;
  posthog?.capture?.(AD_ACCOUNT_CONNECTION_FAILED_EVENT, {
    platform: normalizedPlatform,
    error_message: errorMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH),
    ...(errorCode ? { error_code: errorCode } : {}),
    ...(accountId ? { account_id: accountId } : {}),
  });
  return true;
}

/**
 * Captures a callback failure at most once per browser session for a given
 * failure fingerprint, so refreshing or navigating back to an integration
 * error page does not re-emit the event.
 */
export function captureAdAccountConnectionFailedOnce(
  posthog: PostHogClient,
  platform: string,
  errorMessage: string,
  options: AdAccountConnectionFailureOptions = {}
): boolean {
  const fingerprint = JSON.stringify([
    normalizeAdAccountConnectionPlatform(platform),
    errorMessage,
    options.errorCode ?? null,
    options.accountId ?? null,
  ]);

  if (typeof window !== "undefined") {
    try {
      if (window.sessionStorage.getItem(REPORTED_FAILURE_KEY) === fingerprint) {
        return false;
      }
    } catch {
      // Fall through and capture; analytics state must never block the page.
    }
  }

  const captured = captureAdAccountConnectionFailed(
    posthog,
    platform,
    errorMessage,
    options
  );
  if (captured && typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(REPORTED_FAILURE_KEY, fingerprint);
    } catch {
      // Ignore storage failures.
    }
  }
  return captured;
}

/**
 * Emits the outcome of a set of authorized accounts against a known baseline:
 * accounts not in the baseline were connected, accounts already in it were
 * re-authorized. Returns the split so callers can act on it if needed.
 */
export function captureAuthorizedAdAccounts(
  posthog: PostHogClient,
  platform: string,
  authorizedAccountIds: string[],
  existingAccountIds: string[]
): { connected: string[]; reauthorized: string[] } {
  const existing = new Set(existingAccountIds);
  const connected: string[] = [];
  const reauthorized: string[] = [];

  for (const accountId of authorizedAccountIds) {
    if (existing.has(accountId)) {
      reauthorized.push(accountId);
      captureAdAccountConnectionUpdated(
        posthog,
        platform,
        accountId,
        "reauthorized"
      );
    } else {
      connected.push(accountId);
      captureAdAccountConnected(posthog, platform, accountId);
    }
  }

  return { connected, reauthorized };
}

/**
 * Handles the OAuth success callback: consumes the pre-redirect baseline and
 * emits connected / reauthorized events for the `account_ids` the
 * ad-connector put on the redirect URL. Without a baseline (the user started
 * the flow before the existing accounts had loaded, storage is unavailable,
 * or the page was refreshed) nothing is emitted — we cannot tell new from
 * re-authorized and would rather under-count than mislabel.
 */
export function captureAdAccountOAuthCallbackSuccess(
  posthog: PostHogClient,
  platform: string,
  authorizedAccountIds: string[]
): { connected: string[]; reauthorized: string[] } | null {
  const pendingConnection = consumePendingAdAccountConnection(platform);
  if (!pendingConnection || authorizedAccountIds.length === 0) {
    return null;
  }

  return captureAuthorizedAdAccounts(
    posthog,
    platform,
    authorizedAccountIds,
    pendingConnection.existingAccountIds
  );
}

// ---------------------------------------------------------------------------
// Pending OAuth connection baseline (sessionStorage)
// ---------------------------------------------------------------------------

export function rememberPendingAdAccountConnection(
  platform: string,
  existingAccountIds: string[]
): boolean {
  const normalizedPlatform = normalizeAdAccountConnectionPlatform(platform);
  if (!normalizedPlatform || typeof window === "undefined") {
    return false;
  }

  const pendingConnection: PendingAdAccountConnection = {
    platform: normalizedPlatform,
    existingAccountIds: [...new Set(existingAccountIds.filter(Boolean))],
    createdAt: Date.now(),
  };
  try {
    window.sessionStorage.setItem(
      PENDING_CONNECTION_KEY,
      JSON.stringify(pendingConnection)
    );
    return true;
  } catch {
    return false;
  }
}

export function clearPendingAdAccountConnection(): void {
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(PENDING_CONNECTION_KEY);
    } catch {
      // Analytics state must never block the connection flow.
    }
  }
}

export function consumePendingAdAccountConnection(
  expectedPlatform: string
): PendingAdAccountConnection | null {
  if (typeof window === "undefined") {
    return null;
  }

  let storedValue: string | null;
  try {
    storedValue = window.sessionStorage.getItem(PENDING_CONNECTION_KEY);
  } catch {
    return null;
  }
  if (!storedValue) {
    return null;
  }

  clearPendingAdAccountConnection();

  try {
    const pendingConnection = JSON.parse(
      storedValue
    ) as PendingAdAccountConnection;
    const normalizedExpectedPlatform =
      normalizeAdAccountConnectionPlatform(expectedPlatform);
    const isFresh =
      typeof pendingConnection.createdAt === "number" &&
      Date.now() - pendingConnection.createdAt <= PENDING_EVENT_MAX_AGE_MS;

    if (
      !normalizedExpectedPlatform ||
      pendingConnection.platform !== normalizedExpectedPlatform ||
      !Array.isArray(pendingConnection.existingAccountIds) ||
      !pendingConnection.existingAccountIds.every(
        (accountId) => typeof accountId === "string"
      ) ||
      !isFresh
    ) {
      return null;
    }

    return pendingConnection;
  } catch {
    return null;
  }
}
